import { useCacheRef } from '@doltech/core/lib/hooks/useCacheRef';
import { useElementQuerySelector } from '@doltech/core/lib/hooks/useElementQuerySelector';
import { useScrollCallback } from '@doltech/core/lib/hooks/useScrollCallback';
import { getScrollYOf } from '@doltech/core/lib/hooks/useScrollElement';
import cl from 'classnames';
import { inRange, noop } from 'lodash';
import * as React from 'react';
import { useDebounce, useWindowSize } from 'react-use';
import styled, { css } from 'styled-components';

export interface DomOffsetValue {
  top: number;
  height: number;
  width: number;
  left: number;
  boundaryLeft: number;
  boundaryTop: number;
}

interface StickPosition {
  align: 'left' | 'right' | null;
  gapInPx: number;
  stickElmInfo: DomOffsetValue;
  currentElmInfo: DomOffsetValue;
}

const Main = styled.div.withConfig({
  componentId: 'BlogSticky_Main',
} as any)<{ $stickOnElmPosition?: Partial<StickPosition> }>`
  position: fixed;
  will-change: opacity, transform;
  top: 0;
  transition: opacity 200ms;

  ${(p) =>
    p?.$stickOnElmPosition?.align === 'left' &&
    css`
      left: ${(p.$stickOnElmPosition?.stickElmInfo?.boundaryLeft || 0) -
      p.$stickOnElmPosition.gapInPx -
      (p.$stickOnElmPosition?.currentElmInfo?.width || 752)}px;
    `}
  ${(p) =>
    p?.$stickOnElmPosition?.align === 'right' &&
    css`
      left: ${(p.$stickOnElmPosition?.stickElmInfo?.boundaryLeft || 0) +
      (p.$stickOnElmPosition?.stickElmInfo?.width || 0) +
      p.$stickOnElmPosition.gapInPx}px;
    `}

  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
`;

export interface BlogStickyProps {
  stickyId: string;
  scrollContainerElm: string | HTMLElement | Window;
  stickOnElm: string;
  stickOnElmPosition?: Omit<StickPosition, 'stickElmInfo' | 'currentElmInfo'>;
  hideWhenIntersectWithElms?: string[];
  stickyMode: 'ALWAYS_SHOW' | 'SHOW_AFTER_SCROLL';

  offsetY?: number;

  children: any;
  className?: any;
  onStickyVisibleChange?: (stickyVisible?: boolean) => void;
}

const getOffsetFromTop = (htmlElement: HTMLElement) => {
  if (htmlElement.offsetParent) {
    return htmlElement.offsetTop + getOffsetFromTop(htmlElement.offsetParent as HTMLElement);
  }
  return htmlElement.offsetTop;
};

const getDomOffsetValue = (htmlElement: HTMLElement) => {
  if (!htmlElement) {
    return null;
  }
  const positionCurrentElm = htmlElement.getBoundingClientRect();
  return {
    top: getOffsetFromTop(htmlElement),
    height: htmlElement.offsetHeight,
    left: htmlElement.offsetLeft,
    width: htmlElement.offsetWidth,
    boundaryLeft: positionCurrentElm.left,
    boundaryTop: positionCurrentElm.top,
  };
};

const is2ElementIntersect = (first: DomOffsetValue, second: DomOffsetValue) => {
  const longerOne = first.height > second.height ? first : second;
  const shorterOne = longerOne === first ? second : first;

  return (
    inRange(shorterOne.top, longerOne.top, longerOne.top + longerOne.height) ||
    inRange(shorterOne.top + shorterOne.height, longerOne.top, longerOne.top + longerOne.height)
  );
};

export const BlogSticky = (props: BlogStickyProps) => {
  const {
    stickyId,
    scrollContainerElm,
    stickOnElm,
    stickyMode = 'SHOW_AFTER_SCROLL',
    stickOnElmPosition = { align: null, gapInPx: 0 },
    hideWhenIntersectWithElms = [],
    offsetY = 144,
    className,
    onStickyVisibleChange = noop,

    children,
    ...rest
  } = props;

  const currentElementRef = React.useRef<HTMLElement>();
  const elementToStickToRef = React.useRef<HTMLElement>();
  const hideWhenIntersectWithElmsRef = React.useRef<HTMLElement[]>();

  const [stickyOffset, setStickyOffset] = React.useState(0);
  const [shouldShowSticky, setShouldShowSticky] = React.useState(false);
  const [updatedStickOnElmPosition, setStickOnElmPosition] =
    React.useState<Partial<StickPosition>>(stickOnElmPosition);
  const { getData: getCurrentElementHeight, setData: setCurrentElementHeight } =
    useCacheRef<number>(0);

  const { elementRef: scrollRef } = useElementQuerySelector(scrollContainerElm);
  const findElements = React.useCallback(() => {
    const foundCurrentElm = document.getElementById(stickyId) as HTMLElement;
    if (foundCurrentElm) {
      currentElementRef.current = foundCurrentElm;
    }
    const foundElmToStick = document.querySelector(stickOnElm) as HTMLElement;
    if (foundElmToStick) {
      elementToStickToRef.current = foundElmToStick;
    }
    hideWhenIntersectWithElmsRef.current = hideWhenIntersectWithElms.flatMap((domQueryString) => {
      const arr: HTMLElement[] = [];
      document.querySelectorAll(domQueryString).forEach((it) => arr.push(it as HTMLElement));
      return arr;
    });
  }, [hideWhenIntersectWithElms, stickOnElm, stickyId]);
  const isIntersect = React.useCallback(() => {
    const y = getScrollYOf(scrollRef.current);
    const currentElmInfo = {
      ...getDomOffsetValue(currentElementRef.current),
      top: y + offsetY,
    };
    if (currentElmInfo.height === 0) {
      currentElmInfo.height = getCurrentElementHeight();
    } else {
      setCurrentElementHeight(currentElmInfo.height);
    }

    return hideWhenIntersectWithElmsRef.current.some((elmToHide) => {
      const elmToHideInfo = getDomOffsetValue(elmToHide);
      return is2ElementIntersect(currentElmInfo, elmToHideInfo);
    });
  }, [getCurrentElementHeight, offsetY, scrollRef, setCurrentElementHeight]);
  const calculateStickyOffset = React.useCallback(() => {
    let y = offsetY;
    const stickyElmInfo = getDomOffsetValue(elementToStickToRef.current);
    const scrollY = getScrollYOf(scrollRef.current);
    let showSticky = (scrollY || 0) >= (stickyElmInfo?.top || 0 - offsetY);
    if (stickyMode === 'ALWAYS_SHOW') {
      if ((stickyElmInfo?.top || 0) - (scrollY || 0) > offsetY) {
        y = (stickyElmInfo?.top || 0) - (scrollY || 0);
      }
      showSticky = true;
    }
    const currentShouldShowSticky = showSticky && !isIntersect();

    if (currentShouldShowSticky) {
      setStickyOffset(y);
      setStickOnElmPosition({
        ...stickOnElmPosition,
        stickElmInfo: stickyElmInfo,
        currentElmInfo: getDomOffsetValue(currentElementRef.current),
      });
    }
    setShouldShowSticky(currentShouldShowSticky);
  }, [isIntersect, offsetY, scrollRef, stickOnElmPosition, stickyMode]);

  const onScrollCallback = React.useCallback(() => {
    findElements();
    calculateStickyOffset();
  }, [calculateStickyOffset, findElements]);
  useScrollCallback(scrollRef, onScrollCallback);

  const windowSize = useWindowSize();

  useDebounce(onScrollCallback, 50, [windowSize.width]);

  React.useEffect(() => {
    onStickyVisibleChange(shouldShowSticky);
    setTimeout(() => {
      onScrollCallback();
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowSticky]);

  if (!elementToStickToRef.current)
    return (
      <Main
        id={stickyId}
        style={{ transform: `translateY(${stickyOffset}px)` }}
        className={cl(
          'blog-sticky',
          {
            hidden: !shouldShowSticky,
          },
          className
        )}
        {...rest}
      >
        {children}
      </Main>
    );

  return (
    <Main
      id={stickyId}
      style={{ transform: `translateY(${stickyOffset}px)` }}
      $stickOnElmPosition={updatedStickOnElmPosition}
      className={cl(
        'blog-sticky',
        {
          hidden: !shouldShowSticky,
        },
        className
      )}
      {...rest}
    >
      {children}
    </Main>
  );
};
